import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from '@/context/LanguageContext';
import {
  Bell,
  ShoppingCart,
  AlertTriangle,
  DollarSign,
  Package,
  User,
  Trash2,
  Clock,
  TrendingUp,
  TrendingDown,
  CheckCheck,
} from 'lucide-react';

import api from '../api/axios';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useSocket } from '../context/SocketContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Notification {
  id: number;
  type: string;
  title: string;
  body: string;
  data?: string | Record<string, unknown>;
  is_read: boolean;
  created_at: string;
}

const TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string }> = {
  ORDER_CREATED: { icon: ShoppingCart, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  ORDER_SHIPPED: { icon: Package, color: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-900/30' },
  ORDER_DELIVERED: { icon: CheckCheck, color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  ORDER_CANCELLED: { icon: AlertTriangle, color: 'text-rose-500', bg: 'bg-rose-100 dark:bg-rose-900/30' },
  STOCK_EXPECTED: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  STOCK_RECEIVED: { icon: Package, color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  client_created: { icon: User, color: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-900/30' },
  CLIENT_CREATED: { icon: User, color: 'text-violet-500', bg: 'bg-violet-100 dark:bg-violet-900/30' },
  PAYMENT_RECEIVED: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-200 dark:bg-emerald-900/40' },
  FINANCE_INCOME: { icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  EXPENSE_ALERT: { icon: TrendingDown, color: 'text-rose-500', bg: 'bg-rose-100 dark:bg-rose-900/30' },
  TASK_ASSIGNED: { icon: Bell, color: 'text-indigo-500', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  TASK_COMMENTED: { icon: Bell, color: 'text-indigo-400', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  default: { icon: Bell, color: 'text-indigo-500', bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
};

export default function NotificationsDropdown() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { socket } = useSocket();

  const unreadCount = Array.isArray(notifications) ? notifications.filter((n) => !n.is_read).length : 0;

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      // API returns paginated { data: [...], meta: {...} } OR legacy plain array
      const raw = res.data;
      const data = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      setNotifications(data);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = (notif: Notification) => {
      setNotifications((prev) => {
        if (prev.some((n) => n.id === notif.id)) return prev;
        return [notif, ...prev];
      });

      toast.success(
        <div className="flex flex-col gap-0.5">
          <span className="font-black text-[10px] uppercase tracking-tighter">{notif.title}</span>
          <span className="text-[10px] opacity-70 leading-tight">{notif.body}</span>
        </div>,
        { id: `notif-${notif.id}`, duration: 4000 },
      );
    };

    socket.on('notification', handleNewNotification);
    return () => {
      socket.off('notification', handleNewNotification);
    };
  }, [socket]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function markAsRead(id: number) {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    } catch {
      console.error('Error marking as read');
    }
  };

  async function markAllAsRead() {
    try {
      await api.post('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('Все отмечено как прочитанное', {
        style: { borderRadius: '0.125rem', fontSize: '10px', fontWeight: 'bold' },
      });
    } catch {
      console.error('Error marking all as read');
    }
  };

  async function deleteAll() {
    setIsDeleteDialogOpen(false);
    try {
      await api.delete('/notifications/all');
      setNotifications([]);
      toast.success('Уведомления удалены', {
        style: { borderRadius: '0.125rem', fontSize: '10px', fontWeight: 'bold' },
      });
    } catch (err: unknown) {
      toast.error(err.response?.data?.message || 'Ошибка удаления');
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    markAsRead(notif.id);
    setIsOpen(false);

    if (notif.data) {
      const data = (typeof notif.data === 'string' ? JSON.parse(notif.data) : notif.data) as Record<string, unknown>;
      if (data.orderId) navigate(`/orders/${data.orderId}`);
      else if (data.taskId) navigate(`/tasks`);
      else if (data.url) navigate(String(data.url).replace(window.location.origin, ''));
    }
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => {
            const willOpen = !isOpen;
            setIsOpen(willOpen);
            if (willOpen) fetchNotifications();
          }}
          className="relative p-2 rounded-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-all group"
        >
          <Bell
            className={`w-5 h-5 transition-colors ${unreadCount > 0 ? 'text-blue-600 animate-pulse' : 'text-slate-500 group-hover:text-slate-900'}`}
          />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-[16px] bg-rose-600 text-white text-[8px] font-black rounded-sm flex items-center justify-center border border-white dark:border-slate-900 shadow-xs leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-white dark:bg-slate-950 rounded-sm border dark:border-slate-800 shadow-2xl z-100 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="p-3 border-b dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/50">
              <div className="flex items-center gap-2">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {t('notif.dropdown.title')}
                </h3>
                {notifications.filter((n) => !n.is_read).length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-sm bg-blue-600 text-white text-[9px] font-black">
                    {notifications.filter((n) => !n.is_read).length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={markAllAsRead}
                  className="p-1.5 rounded-sm hover:bg-white dark:hover:bg-slate-800 text-slate-400 hover:text-blue-600 transition-colors"
                  title="{t('notif.dropdown.mark_read')}"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setIsDeleteDialogOpen(true)}
                  className="p-1.5 rounded-sm hover:bg-white dark:hover:bg-slate-800 text-slate-400 hover:text-rose-600 transition-colors"
                  title="Удалить все"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
              {notifications.length === 0 ? (
                <div className="py-16 text-center text-muted-foreground flex flex-col items-center gap-3">
                  <div className="p-4 rounded-sm bg-slate-50 dark:bg-slate-900/50">
                    <Bell className="w-8 h-8 opacity-20" />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-40">Пока пусто</p>
                    <p className="text-[9px] mt-1 opacity-30 font-medium">Здесь появятся важные события</p>
                  </div>
                </div>
              ) : (
                <div className="divide-y dark:divide-slate-800/50">
                  {notifications.map((notif) => {
                    const config = TYPE_CONFIG[notif.type] || TYPE_CONFIG.default;
                    const Icon = config.icon;
                    return (
                      <div
                        key={notif.id}
                        className={`px-4 py-4 hover:bg-slate-50 dark:hover:bg-slate-900/40 transition-colors cursor-pointer group relative ${!notif.is_read ? 'bg-blue-50/20 dark:bg-blue-900/5' : ''}`}
                        onClick={() => handleNotificationClick(notif)}
                      >
                        <div className="flex items-start gap-4">
                          <div
                            className={`p-2 rounded-sm ${config.bg} shrink-0 shadow-xs border dark:border-slate-800/50`}
                          >
                            <Icon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <span
                                className={`font-black text-[11px] uppercase tracking-tighter leading-tight ${!notif.is_read ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500'}`}
                              >
                                {notif.title}
                              </span>
                              <div className="flex flex-col items-end gap-1 shrink-0">
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-tight flex items-center gap-1">
                                  <Clock className="w-2.5 h-2.5" />
                                  {(() => {
                                    try {
                                      const date = new Date(notif.created_at);
                                      if (isNaN(date.getTime())) return 'Неизвестно';
                                      return formatDistanceToNow(date, { addSuffix: true, locale: ru });
                                    } catch {
                                      return 'Неизвестно';
                                    }
                                  })()}
                                </span>
                                {!notif.is_read && (
                                  <span className="w-1.5 h-1.5 bg-blue-600 rounded-sm shadow-[0_0_8px_rgba(37,99,235,0.6)] animate-pulse" />
                                )}
                              </div>
                            </div>
                            <p
                              className={`text-[11px] mt-1.5 line-clamp-2 leading-snug font-medium ${!notif.is_read ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}`}
                            >
                              {notif.body}
                            </p>
                            <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              {!notif.is_read && (
                                <button className="text-[9px] font-black uppercase tracking-widest text-blue-600 hover:text-blue-700">
                                  Прочитать
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {notifications.length > 0 && (
              <div className="p-2 border-t dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 flex justify-center">
                <button
                  onClick={() => {
                    navigate('/notifications');
                    setIsOpen(false);
                  }}
                  className="text-[9px] font-black uppercase tracking-[2px] text-slate-400 hover:text-blue-600 transition-colors py-1"
                >
                  Посмотреть все
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm border-slate-200 dark:border-slate-800 max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xs font-black uppercase tracking-widest text-slate-900 dark:text-white">
              Удалить все уведомления?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Все уведомления будут безвозвратно удалены.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2">
            <AlertDialogCancel className="rounded-sm text-[9px] font-black uppercase tracking-widest border-slate-200 dark:border-slate-800 mt-0">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                deleteAll();
              }}
              className="rounded-sm text-[9px] font-black uppercase tracking-widest bg-rose-600 hover:bg-rose-700 text-white"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
